import { AnalyticsBrowser } from "@segment/analytics-next";
import { captureException } from "@sentry/react";

import {
  experimentAssignmentsEnabled,
  stampExperimentProperties,
} from "@lib/analytics/experimentAssignments";
import { scrubEvent } from "@lib/analytics/scrubEvent";
import { isCheckoutSourcePath } from "@lib/publicPaths";

// Module-level singleton AnalyticsBrowser instance.
//
// Previously SegmentContext.tsx called `AnalyticsBrowser.load(config)`
// inside the SegmentProvider component, which meant Segment didn't
// start loading until React mounted and the provider rendered. That
// also tied any usage of Segment to React's component lifecycle.
//
// Lifting the instance up to module scope lets non-React code (notably
// main.tsx, where we fire begin_checkout early for funnel-timing
// parity with the legacy /doSignup) reuse the same Segment instance.
// AnalyticsBrowser.load() returns a thenable that internally queues
// events until the underlying analytics-js SDK is loaded from CDN, so
// callers don't need to await anything — they just call .track() and
// the event is delivered as soon as the SDK is ready.
//
// Note: this file is imported eagerly by both main.tsx and
// SegmentContext.tsx, so the load() call runs as soon as the JS
// bundle parses (instead of waiting for SegmentProvider to mount).
// That's the desired behavior — earlier Segment availability is the
// whole point.
// Public, non-member pages (/checkout, /gifts, /redeem) send analytics to
// the checkout/legacy Segment source — the same source the checkout backend
// (omg-api) and the legacy omgyes-server checkout write to — so all
// checkout/gift events and their downstream GA4 / Meta CAPI / Iterable
// mappings live in one place. Member pages use the New Member Home source.
// Exception: the payment-first checkout variant also reached at
// /members/checkout/pf routes to the checkout source too, so the variant's
// revenue/ecommerce props aren't stripped by the member-source PII scrub —
// see isCheckoutSourcePath (OMG-2120). Basename still keys off isPublicPath.
//
// The source is chosen from the *entry* path at load time. Because the
// post-checkout flow hard-redirects to /members/ (a full reload), the legacy
// source stays scoped to the public pages within a session. Falls back to the
// New Member Home key if the checkout key isn't configured for a stage, so a
// missing env var degrades to "everything to NMH" rather than loading Segment
// with an empty write key.
const writeKey =
  isCheckoutSourcePath(window.location.pathname) &&
  import.meta.env.VITE_SEGMENT_CHECKOUT_WRITE_KEY
    ? import.meta.env.VITE_SEGMENT_CHECKOUT_WRITE_KEY
    : import.meta.env.VITE_SEGMENT_WRITE_KEY;

export const segmentInstance = AnalyticsBrowser.load({ writeKey });

// PII data-minimization (OMG-1945). Registered whenever isCheckoutSourcePath is
// false — i.e. ordinary member pages, but NOT the /members/checkout/pf PF-variant
// exception (which uses the checkout source). The checkout/legacy source feeds
// revenue destinations (Meta CAPI / GA4 / Iterable) and is intentionally left
// unscrubbed. Gated on the entry path for the same reason the source is: it's an
// entry-time decision, and the post-checkout flow hard-reloads into /members/
// (PostCheckout.tsx) which re-evaluates it back to the member source + scrub.
//
// addSourceMiddleware returns a promise, but the browser bootstrap awaits
// middleware registration before replaying buffered pre-ready events, so
// registering synchronously here (before main.tsx fires anything) covers every
// event. The scrub mutates payload.obj in place; next(payload) forwards it.
if (!isCheckoutSourcePath(window.location.pathname)) {
  segmentInstance
    .addSourceMiddleware(({ payload, next }) => {
      // A throw here would skip next() and make the SDK silently cancel the
      // event — a scrub bug must never turn into lost analytics. Catch, report,
      // and forward; worst case an event ships unscrubbed rather than vanishing.
      try {
        scrubEvent(payload.obj);
      } catch (err) {
        captureException(err);
      }
      next(payload);
    })
    // If registration itself rejects (e.g. SDK fails to load), member events
    // would flow unscrubbed — surface that rather than swallowing it.
    .catch(captureException);
}

// Experiment arms on checkout-source events: the Refund Guarantee Test
// (OMG-2619, rg26_arm / rg26_assigner) and the Organic Funnel Test (OMG-2684,
// of26_arm). Stamped onto every page and track event from the public
// checkout/gifts/redeem pages (and the PF variant) so Mixpanel can read the
// checkout funnel per arm. Member pages are out of scope: their scrub above
// allowlists properties and would strip these anyway. Registered only when
// the checkout body's own send gate is on, so a stage with the gate off is
// byte-identical to before. The cookies are read per event inside the stamp,
// not once here, so an arm written after load is still picked up.
if (
  isCheckoutSourcePath(window.location.pathname) &&
  experimentAssignmentsEnabled()
) {
  segmentInstance
    .addSourceMiddleware(({ payload, next }) => {
      // Same rule as the scrub: a stamp bug must never cost the event.
      try {
        stampExperimentProperties(payload.obj);
      } catch (err) {
        captureException(err);
      }
      next(payload);
    })
    .catch(captureException);
}

// Exposed so SegmentContext reflects the source that was actually loaded.
export const segmentWriteKey = writeKey;
